import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useFetch } from '../../../components/component-items/helpers'
import { Link } from 'react-router-dom'

// Components
import Pager, { Pages } from '../../../components/component-items/pager'
import CardLoading from '../../../components/component-items/loading-popover'
import GlobalStyles from '../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../components/component-items/status-box'
import { TableCell, THSort } from '../../../components/component-items/datatable'
import { SearchBox } from '../../../components/component-items/search'
import { AdvancedSelect, filterOptionsURLParams } from '../../../components/component-items/advanced-select'
import { PermissionGate } from '../../../components/rbac'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { TableRow, ViewToggle, ViewToggleBtn, ChevronBtn, ContainerCardToolbar } from './styles'
import { globalState } from '../../../store'

// Modals (lazy loaded)
const CreateContainerModal = React.lazy(() => import('./modals/create-container-modal'))
const PrintLabelModal = React.lazy(() => import('./modals/print-label-modal'))

// Types
import type { LogisticUnit, ContainerType } from './types'
import { STATE_COLOR_MAP, STATE_DISPLAY_MAP } from './types'

// ── Filter option definitions for AdvancedSelect ────────────────────────────
const STATE_OPTIONS = [
  { value: 'created', label: 'Created' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'empty', label: 'Empty' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'retired', label: 'Retired' },
]

const buildFilterOptions = (containerTypes: ContainerType[]) => [
  {
    label: 'State',
    type: 'multi-select',
    options: STATE_OPTIONS,
    value: [] as any[],
    selected: false,
  },
  {
    label: 'Container Type',
    type: 'multi-select',
    options: containerTypes.map((ct) => ({ value: String(ct.id), label: ct.name })),
    value: [] as any[],
    selected: false,
  },
]

export const ContainerList = () => {
  const {
    state: { actAs, userIsWarehouse },
  } = globalState()
  const [page, setPage] = useState(1)
  const tableRowOptions = [20, 50, 100, 250, 500]
  const [rows, setRows] = useState(tableRowOptions[0])
  const [sort, setSort] = useState({})
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false)

  // Checkbox selection
  const [selectedLpns, setSelectedLpns] = useState<string[]>([])

  // View toggle
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list')

  // Tree: expanded nodes (only used in tree view)
  const [expandedLpns, setExpandedLpns] = useState<Set<string>>(new Set())

  // Fetch container types for the filter dropdown
  const typesRes: any = useFetch('/api/inventory/container-type/', {})
  const containerTypes: ContainerType[] = typesRes.loaded ? typesRes.response.results || typesRes.response || [] : []

  // ── AdvancedSelect filter state ───────────────────────────────────────────
  const [filterOptions, setFilterOptions] = useState<any[]>(buildFilterOptions([]))

  // Rebuild type options when containerTypes load
  useEffect(() => {
    if (containerTypes.length > 0) {
      setFilterOptions((prev: any[]) => {
        const next = [...prev]
        const typeIdx = next.findIndex((f: any) => f.label === 'Container Type')
        if (typeIdx >= 0) {
          next[typeIdx] = {
            ...next[typeIdx],
            options: containerTypes.map((ct) => ({ value: String(ct.id), label: ct.name })),
          }
        }
        return next
      })
    }
  }, [containerTypes.length])

  useEffect(() => {
    if (filterOptions.some((f: any) => f.type === 'multi-select' && f.selected && !f.value?.length)) {
      setFilterOptions((prev: any[]) =>
        prev.map((f: any) =>
          f.type === 'multi-select' && f.selected && !f.value?.length ? { ...f, selected: false } : f
        )
      )
    }
  }, [filterOptions])

  // Derive active filter values from filterOptions
  const activeStateValues = useMemo(() => {
    const stateOpt = filterOptions.find((f: any) => f.label === 'State')
    if (stateOpt?.selected && stateOpt.value?.length) {
      return stateOpt.value.map((v: any) => v.value)
    }
    return []
  }, [filterOptions])

  const activeTypeValues = useMemo(() => {
    const typeOpt = filterOptions.find((f: any) => f.label === 'Container Type')
    if (typeOpt?.selected && typeOpt.value?.length) {
      return typeOpt.value.map((v: any) => v.value)
    }
    return []
  }, [filterOptions])

  // Build API URL with filters (for real backend)
  const filterParams = filterOptionsURLParams(filterOptions)
  let url = '/api/inventory/logistic-unit/?limit=' + rows + '&offset=' + (page - 1) * rows + '&search=' + search
  if (filterParams) url += '&' + filterParams

  const res: any = useFetch(url, {})
  const containers: LogisticUnit[] = res.loaded ? res.response.results || [] : []

  useEffect(() => {
    setLoading(!res.loaded)
  }, [res.loaded])

  // Clear selection when data changes
  useEffect(() => {
    setSelectedLpns([])
  }, [page, rows, search, filterOptions, sort])

  // Reset to first page when filters, search, or rows change
  useEffect(() => {
    setPage(1)
  }, [filterOptions, search, rows])

  const total = res.loaded ? res.response.count || 0 : 0
  const totalPages = res.loaded && total ? Math.ceil(total / rows) : 1

  // Tree view: group containers by parent
  const { roots, childrenMap } = useMemo(() => {
    if (viewMode !== 'tree' || containers.length === 0) {
      return { roots: containers, childrenMap: new Map<string, LogisticUnit[]>() }
    }
    const map = new Map<string, LogisticUnit[]>()
    const rootItems: LogisticUnit[] = []
    for (const c of containers) {
      if (!c.parent_lpn) {
        rootItems.push(c)
      } else {
        const siblings = map.get(c.parent_lpn) || []
        siblings.push(c)
        map.set(c.parent_lpn, siblings)
      }
    }
    return { roots: rootItems, childrenMap: map }
  }, [containers, viewMode])

  // Auto-expand level 1 when entering tree view
  useEffect(() => {
    if (viewMode === 'tree' && roots.length > 0) {
      setExpandedLpns((prev) => {
        // Only auto-expand if nothing is expanded yet (first switch or data reload)
        if (prev.size > 0) return prev
        const level1 = new Set<string>()
        for (const root of roots) {
          if (childrenMap.has(root.lpn)) {
            level1.add(root.lpn)
          }
        }
        return level1
      })
    }
    if (viewMode === 'list') {
      setExpandedLpns((prev) => {
        // Only clear if there's something to clear (prevent infinite loop from new Set() every render)
        if (prev.size > 0) return new Set()
        return prev
      })
    }
    // Only depend on viewMode - childrenMap causes infinite loops because it's a new Map reference on every containers change
  }, [viewMode])

  // ── Tree expand/collapse helpers ──────────────────────────────────────────
  const toggleExpand = useCallback((lpn: string) => {
    setExpandedLpns((prev) => {
      const next = new Set(prev)
      if (next.has(lpn)) {
        next.delete(lpn)
      } else {
        next.add(lpn)
      }
      return next
    })
  }, [])

  const hasChildren = useCallback((lpn: string) => childrenMap.has(lpn), [childrenMap])

  // Compute visible LPNs (in tree mode, only expanded subtrees are visible)
  const visibleLpns = useMemo(() => {
    if (viewMode === 'list') return containers.map((c) => c.lpn)
    const result: string[] = []
    const collect = (items: LogisticUnit[]) => {
      for (const item of items) {
        result.push(item.lpn)
        if (expandedLpns.has(item.lpn)) {
          const children = childrenMap.get(item.lpn)
          if (children) collect(children)
        }
      }
    }
    collect(roots)
    return result
  }, [viewMode, containers, roots, childrenMap, expandedLpns])

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLpns(visibleLpns)
    } else {
      setSelectedLpns([])
    }
  }

  const handleSelectOne = (lpn: string, checked: boolean) => {
    if (checked) {
      setSelectedLpns((prev) => [...prev, lpn])
    } else {
      setSelectedLpns((prev) => prev.filter((l) => l !== lpn))
    }
  }

  const allSelected = visibleLpns.length > 0 && selectedLpns.length === visibleLpns.length
  const hasCompanyContext = !userIsWarehouse || !!actAs

  // ── Row rendering ─────────────────────────────────────────────────────────
  const renderRow = (item: LogisticUnit, depth: number = 0, isLast: boolean = true) => {
    const isParent = hasChildren(item.lpn)
    const isExpanded = expandedLpns.has(item.lpn)
    const inTreeMode = viewMode === 'tree'

    return (
      <TableRow
        key={item.lpn}
        $depth={inTreeMode ? depth : 0}
        $isParent={inTreeMode && isParent}
        $isExpanded={inTreeMode && isExpanded}
        $isLastChild={inTreeMode && depth > 0 && isLast}
      >
        <PermissionGate segment="inventory" minLevel="operator">
          <TableCell center>
            <input
              type="checkbox"
              checked={selectedLpns.includes(item.lpn)}
              onChange={(e) => handleSelectOne(item.lpn, e.target.checked)}
            />
          </TableCell>
        </PermissionGate>
        <TableCell>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              paddingLeft: inTreeMode ? depth * 20 : 0,
            }}
          >
            {inTreeMode && isParent && (
              <ChevronBtn onClick={() => toggleExpand(item.lpn)} title={isExpanded ? 'Collapse' : 'Expand'}>
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
              </ChevronBtn>
            )}
            {inTreeMode && !isParent && depth > 0 && <span style={{ display: 'inline-block', width: 20 }} />}
            <Link to={`/inventory/container/${item.lpn}/`}>{item.lpn}</Link>
          </span>
        </TableCell>
        <TableCell input={item.company_name || '\u2014'} />
        <TableCell center input={item.container_type_name || '\u2014'} />
        <TableCell center>
          <StatusBoxStyled
            className={STATE_COLOR_MAP[item.state] || 'silver'}
            style={{ width: 100, margin: '0.5em auto' }}
          >
            {STATE_DISPLAY_MAP[item.state] || item.state}
          </StatusBoxStyled>
        </TableCell>
        <TableCell input={item.location_barcode} />
        <TableCell center input={`${item.total_quantity} units`} />
        <TableCell
          center
          input={
            item.parent_lpn ? <Link to={`/inventory/container/${item.parent_lpn}/`}>{item.parent_lpn}</Link> : '\u2014'
          }
        />
        <TableCell center input={item.created ? new Date(item.created).toLocaleDateString() : '\u2014'} />
      </TableRow>
    )
  }

  const renderRows = () => {
    if (viewMode === 'tree') {
      const result: React.ReactNode[] = []
      const renderTree = (items: LogisticUnit[], depth: number) => {
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          const isLast = i === items.length - 1
          result.push(renderRow(item, depth, isLast))
          // Only render children if this node is expanded
          if (expandedLpns.has(item.lpn)) {
            const children = childrenMap.get(item.lpn)
            if (children) {
              renderTree(children, depth + 1)
            }
          }
        }
      }
      renderTree(roots, 0)
      return result
    }
    return containers.map((item: LogisticUnit) => renderRow(item, 0))
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            Containers<small>{total} Total</small>
          </h3>
        </GlobalStyles.CardTitle>
        <ContainerCardToolbar>
          <ViewToggle>
            <ViewToggleBtn $active={viewMode === 'list'} onClick={() => setViewMode('list')}>
              List
            </ViewToggleBtn>
            <ViewToggleBtn $active={viewMode === 'tree'} onClick={() => setViewMode('tree')}>
              Tree
            </ViewToggleBtn>
          </ViewToggle>
          <PermissionGate segment="inventory" minLevel="operator">
            {selectedLpns.length > 0 && (
              <GlobalStyles.Button className="secondary" onClick={() => setShowPrintLabelModal(true)}>
                Print Labels ({selectedLpns.length})
              </GlobalStyles.Button>
            )}
            {hasCompanyContext && (
              <GlobalStyles.Button onClick={() => setShowCreateModal(true)}>Add Container</GlobalStyles.Button>
            )}
          </PermissionGate>
          <AdvancedSelect placeholder="Filter" {...{ filterOptions, setFilterOptions }} width={280} />
          <SearchBox {...{ search, setSearch, loading, setLoading, page, setPage }} setFocus updateURL />
        </ContainerCardToolbar>
      </GlobalStyles.CardHeader>
      <div style={{ minHeight: 500, overflowX: 'auto' }}>
        <GlobalStyles.DataTable style={{ minWidth: 1000 }}>
          <thead>
            <tr>
              <PermissionGate segment="inventory" minLevel="operator">
                <th style={{ textAlign: 'center', width: 40 }}>
                  <input type="checkbox" checked={allSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
                </th>
              </PermissionGate>
              <THSort title={'LPN'} sortKey={'lpn'} {...{ sort, setSort }} />
              <THSort title={'Company'} sortKey={'company__name'} {...{ sort, setSort }} />
              <THSort title={'Type'} sortKey={'container_type__name'} center {...{ sort, setSort }} />
              <THSort title={'State'} sortKey={'state'} center {...{ sort, setSort }} />
              <THSort title={'Location'} sortKey={'location__barcode'} {...{ sort, setSort }} />
              <THSort title={'Contents'} center {...{ sort, setSort }} />
              <THSort title={'Parent'} sortKey={'parent__lpn'} center {...{ sort, setSort }} />
              <THSort title={'Created'} sortKey={'created'} center {...{ sort, setSort }} />
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </GlobalStyles.DataTable>
      </div>
      <GlobalStyles.CardFooter>
        <Pages page={page} setPage={setPage} total={totalPages} />
        <Pager options={tableRowOptions} page={page} total={total} rows={rows} setRows={setRows} />
      </GlobalStyles.CardFooter>
      {!res.loaded ? <CardLoading text={res.placeholder} error={res.error} /> : null}
      {showCreateModal && (
        <React.Suspense fallback={null}>
          <CreateContainerModal show={showCreateModal} onHide={() => setShowCreateModal(false)} />
        </React.Suspense>
      )}
      {showPrintLabelModal && selectedLpns.length > 0 && (
        <React.Suspense fallback={null}>
          <PrintLabelModal
            lpns={selectedLpns}
            show={showPrintLabelModal}
            onHide={() => {
              setShowPrintLabelModal(false)
              setSelectedLpns([])
            }}
          />
        </React.Suspense>
      )}
    </GlobalStyles.FullPageCard>
  )
}
